<ngx-spinner type="ball-atom"></ngx-spinner>
<div class="component-container">
  <div class="component-header">
    <div class="header-left">
      <button mat-icon-button (click)="back()" matTooltip="Back to portal">
        <mat-icon>chevron_left</mat-icon>
      </button>
      <div class="component-header__text">
        <h2>Data for Statistics</h2>
        <span class="header-subtitle">Build a statistical report in seven steps</span>
      </div>
    </div>
    <button mat-stroked-button type="button" class="restart-button" (click)="resetAll()"
            matTooltip="Reset every step and start a fresh report from step 1.">
      <mat-icon>restart_alt</mat-icon>
      Start new search
    </button>
  </div>

  <mat-stepper #stepper [linear]="true" labelPosition="bottom" class="stats-stepper">

    <!-- ──────────────────────────────────────────────────────── STEP 1: MODE -->
    <mat-step [stepControl]="modeForm">
      <ng-template matStepLabel>Mode</ng-template>

      <div class="step-actions">
        <span class="spacer"></span>
        <button mat-raised-button color="primary" matStepperNext [disabled]="modeForm.invalid">
          Next <mat-icon iconPositionEnd>arrow_forward</mat-icon>
        </button>
      </div>

      <form [formGroup]="modeForm" class="step-content component-box">
        <h3 class="step-title">How would you like to query the data?</h3>
        <p class="step-hint">Time series spans a range of years; a static request targets a single year.</p>

        <mat-radio-group formControlName="mode" class="selection-cards">
          <label class="selection-card" [class.selected]="mode === 'time-series'">
            <mat-radio-button value="time-series" class="selection-card__radio"></mat-radio-button>
            <mat-icon class="selection-card__icon">timeline</mat-icon>
            <div class="selection-card__body">
              <div class="selection-card__title">Time Series</div>
              <div class="selection-card__desc">Compare values across a range of years.</div>
            </div>
          </label>
          <label class="selection-card" [class.selected]="mode === 'static'">
            <mat-radio-button value="static" class="selection-card__radio"></mat-radio-button>
            <mat-icon class="selection-card__icon">center_focus_strong</mat-icon>
            <div class="selection-card__body">
              <div class="selection-card__title">Static Request</div>
              <div class="selection-card__desc">A single year snapshot.</div>
            </div>
          </label>
        </mat-radio-group>
      </form>
    </mat-step>

    <!-- ──────────────────────────────────────────────────────── STEP 2: YEARS -->
    <mat-step [stepControl]="yearsForm">
      <ng-template matStepLabel>Year(s)</ng-template>

      <div class="step-actions">
        <button mat-stroked-button matStepperPrevious>
          <mat-icon>arrow_back</mat-icon> Back
        </button>
        <span class="spacer"></span>
        <button mat-raised-button color="primary" matStepperNext [disabled]="yearsForm.invalid">
          Next <mat-icon iconPositionEnd>arrow_forward</mat-icon>
        </button>
      </div>

      <form [formGroup]="yearsForm" class="step-content component-box">
        @if (mode === 'time-series') {
          <h3 class="step-title">Select the year range</h3>
          <p class="step-hint">Both endpoints are inclusive.</p>
          <div class="year-fields">
            <mat-form-field appearance="fill">
              <mat-label>From Measurement</mat-label>
              <mat-select formControlName="yearFrom">
                @for (y of years; track y) { <mat-option [value]="y">{{ y }}</mat-option> }
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="fill">
              <mat-label>To Measurement</mat-label>
              <mat-select formControlName="yearTo">
                @for (y of years; track y) { <mat-option [value]="y">{{ y }}</mat-option> }
              </mat-select>
            </mat-form-field>
            <button mat-stroked-button type="button" (click)="selectAllYears()"
                    matTooltip="Set the range to span every accessible measurement year.">
              <mat-icon>select_all</mat-icon>
              Select all years
            </button>
          </div>
        } @else if (mode === 'static') {
          <h3 class="step-title">Select the year</h3>
          <div class="year-fields">
            <mat-form-field appearance="fill">
              <mat-label>Measurement</mat-label>
              <mat-select formControlName="year">
                @for (y of years; track y) { <mat-option [value]="y">{{ y }}</mat-option> }
              </mat-select>
            </mat-form-field>
          </div>
        } @else {
          <p class="step-hint">Please pick a mode first.</p>
        }
      </form>
    </mat-step>

    <!-- ──────────────────────────────────────────────────────── STEP 3: COMPANIES -->
    <mat-step [stepControl]="companiesForm">
      <ng-template matStepLabel>Companies</ng-template>

      <div class="step-actions">
        <button mat-stroked-button matStepperPrevious>
          <mat-icon>arrow_back</mat-icon> Back
        </button>
        <span class="spacer"></span>
        <button mat-raised-button color="primary" matStepperNext [disabled]="companiesForm.invalid"
          [matTooltip]="companiesForm.invalid ? 'Select at least one company' : ''">
          Next <mat-icon iconPositionEnd>arrow_forward</mat-icon>
        </button>
      </div>

      <div class="step-content component-box">
        <h3 class="step-title">Search and select one or more companies</h3>

        <form class="search-form" (ngSubmit)="runCompanySearch()">
          <div class="search-fields">
            <mat-form-field appearance="fill">
              <mat-label>Country</mat-label>
              <mat-select [ngModel]="companySearch().countryId" name="countryId"
                          (ngModelChange)="patchCompanySearch({ countryId: $event })">
                <mat-option [value]="null">Any</mat-option>
                @for (c of countries(); track c.id) {
                  <mat-option [value]="c.id">{{ c.name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <!-- Name: operator + value -->
            <div class="combo-field">
              <mat-form-field appearance="fill" class="combo-field__mode">
                <mat-label>Name</mat-label>
                <mat-select [ngModel]="companySearch().companyNameMode" name="companyNameMode"
                            (ngModelChange)="patchCompanySearch({ companyNameMode: $event })">
                  @for (m of textMatchModes; track m.value) {
                    <mat-option [value]="m.value">{{ m.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="fill" class="combo-field__value">
                <mat-label>Value</mat-label>
                <input matInput
                       [ngModel]="companySearch().companyName" name="companyName"
                       (ngModelChange)="patchCompanySearch({ companyName: $event })" />
              </mat-form-field>
            </div>

            <mat-form-field appearance="fill">
              <mat-label>Listing Status</mat-label>
              <mat-select [ngModel]="companySearch().listingType" name="listingType"
                          (ngModelChange)="patchCompanySearch({ listingType: $event })">
                @for (l of listingTypes; track l.value) {
                  <mat-option [value]="l.value">{{ l.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <div class="country-multi-select">
              <mat-form-field appearance="fill" class="country-multi-select__field">
                <mat-label>Industry</mat-label>
                <mat-select multiple
                            [ngModel]="companySearch().industryIds" name="industryIds"
                            (ngModelChange)="onIndustryChange($event)">
                  @for (i of topIndustries(); track i.id) {
                    <mat-option [value]="i.id">{{ i.name }}</mat-option>
                  }
                  <mat-select-trigger>
                    @if (companySearch().industryIds.length > 0) {
                      {{ getIndustryName(companySearch().industryIds[0]) }}
                      @if (companySearch().industryIds.length > 1) {
                        <span class="additional-count">+{{ companySearch().industryIds.length - 1 }} more</span>
                      }
                    }
                  </mat-select-trigger>
                </mat-select>
              </mat-form-field>
              <button mat-icon-button type="button" (click)="selectAllIndustries()"
                      matTooltip="Select all industries" tabindex="-1">
                <mat-icon>select_all</mat-icon>
              </button>
              <button mat-icon-button type="button" (click)="deselectAllIndustries()"
                      matTooltip="Deselect all industries" tabindex="-1">
                <mat-icon>deselect</mat-icon>
              </button>
            </div>

            <div class="country-multi-select">
              <mat-form-field appearance="fill" class="country-multi-select__field">
                <mat-label>Super Sector</mat-label>
                <mat-select multiple
                            [ngModel]="companySearch().superSectorIds" name="superSectorIds"
                            (ngModelChange)="onSuperSectorChange($event)"
                            [disabled]="companySearch().industryIds.length === 0 || superSectorOptions().length === 0">
                  @for (s of superSectorOptions(); track s.id) {
                    <mat-option [value]="s.id">{{ s.name }}</mat-option>
                  }
                  <mat-select-trigger>
                    @if (companySearch().superSectorIds.length > 0) {
                      {{ getIndustryName(companySearch().superSectorIds[0]) }}
                      @if (companySearch().superSectorIds.length > 1) {
                        <span class="additional-count">+{{ companySearch().superSectorIds.length - 1 }} more</span>
                      }
                    }
                  </mat-select-trigger>
                </mat-select>
              </mat-form-field>
              <button mat-icon-button type="button" (click)="selectAllSuperSectors()"
                      [disabled]="superSectorOptions().length === 0"
                      matTooltip="Select all super sectors" tabindex="-1">
                <mat-icon>select_all</mat-icon>
              </button>
              <button mat-icon-button type="button" (click)="deselectAllSuperSectors()"
                      matTooltip="Deselect all super sectors" tabindex="-1">
                <mat-icon>deselect</mat-icon>
              </button>
            </div>

            <div class="country-multi-select">
              <mat-form-field appearance="fill" class="country-multi-select__field">
                <mat-label>Sector</mat-label>
                <mat-select multiple
                            [ngModel]="companySearch().sectorIds" name="sectorIds"
                            (ngModelChange)="onSectorChange($event)"
                            [disabled]="companySearch().superSectorIds.length === 0 || sectorOptions().length === 0">
                  @for (s of sectorOptions(); track s.id) {
                    <mat-option [value]="s.id">{{ s.name }}</mat-option>
                  }
                  <mat-select-trigger>
                    @if (companySearch().sectorIds.length > 0) {
                      {{ getIndustryName(companySearch().sectorIds[0]) }}
                      @if (companySearch().sectorIds.length > 1) {
                        <span class="additional-count">+{{ companySearch().sectorIds.length - 1 }} more</span>
                      }
                    }
                  </mat-select-trigger>
                </mat-select>
              </mat-form-field>
              <button mat-icon-button type="button" (click)="selectAllSectors()"
                      [disabled]="sectorOptions().length === 0"
                      matTooltip="Select all sectors" tabindex="-1">
                <mat-icon>select_all</mat-icon>
              </button>
              <button mat-icon-button type="button" (click)="deselectAllSectors()"
                      matTooltip="Deselect all sectors" tabindex="-1">
                <mat-icon>deselect</mat-icon>
              </button>
            </div>

            <div class="country-multi-select">
              <mat-form-field appearance="fill" class="country-multi-select__field">
                <mat-label>Sub Sector</mat-label>
                <mat-select multiple
                            [ngModel]="companySearch().subSectorIds" name="subSectorIds"
                            (ngModelChange)="onSubSectorChange($event)"
                            [disabled]="companySearch().sectorIds.length === 0 || subSectorOptions().length === 0">
                  @for (s of subSectorOptions(); track s.id) {
                    <mat-option [value]="s.id">{{ s.name }}</mat-option>
                  }
                  <mat-select-trigger>
                    @if (companySearch().subSectorIds.length > 0) {
                      {{ getIndustryName(companySearch().subSectorIds[0]) }}
                      @if (companySearch().subSectorIds.length > 1) {
                        <span class="additional-count">+{{ companySearch().subSectorIds.length - 1 }} more</span>
                      }
                    }
                  </mat-select-trigger>
                </mat-select>
              </mat-form-field>
              <button mat-icon-button type="button" (click)="selectAllSubSectors()"
                      [disabled]="subSectorOptions().length === 0"
                      matTooltip="Select all sub sectors" tabindex="-1">
                <mat-icon>select_all</mat-icon>
              </button>
              <button mat-icon-button type="button" (click)="deselectAllSubSectors()"
                      matTooltip="Deselect all sub sectors" tabindex="-1">
                <mat-icon>deselect</mat-icon>
              </button>
            </div>

            <div class="country-multi-select">
              <mat-form-field appearance="fill" class="country-multi-select__field">
                <mat-label>Stock Exchange</mat-label>
                <mat-select multiple
                            [ngModel]="companySearch().stockExchangeIds" name="stockExchangeIds"
                            (ngModelChange)="onStockExchangeChange($event)">
                  @for (s of stockExchanges(); track s.id) {
                    <mat-option [value]="s.id">{{ s.name }}</mat-option>
                  }
                  <mat-select-trigger>
                    @if (companySearch().stockExchangeIds.length > 0) {
                      {{ getStockExchangeName(companySearch().stockExchangeIds[0]) }}
                      @if (companySearch().stockExchangeIds.length > 1) {
                        <span class="additional-count">+{{ companySearch().stockExchangeIds.length - 1 }} more</span>
                      }
                    }
                  </mat-select-trigger>
                </mat-select>
              </mat-form-field>
              <button mat-icon-button type="button" (click)="selectAllStockExchanges()"
                      matTooltip="Select all stock exchanges" tabindex="-1">
                <mat-icon>select_all</mat-icon>
              </button>
              <button mat-icon-button type="button" (click)="deselectAllStockExchanges()"
                      matTooltip="Deselect all stock exchanges" tabindex="-1">
                <mat-icon>deselect</mat-icon>
              </button>
            </div>

            <div class="country-multi-select">
              <mat-form-field appearance="fill" class="country-multi-select__field">
                <mat-label>Index</mat-label>
                <mat-select multiple
                            [ngModel]="companySearch().stockIndexIds" name="stockIndexIds"
                            (ngModelChange)="onStockIndexChange($event)">
                  @for (s of stockIndices(); track s.id) {
                    <mat-option [value]="s.id">{{ s.name }}</mat-option>
                  }
                  <mat-select-trigger>
                    @if (companySearch().stockIndexIds.length > 0) {
                      {{ getStockIndexName(companySearch().stockIndexIds[0]) }}
                      @if (companySearch().stockIndexIds.length > 1) {
                        <span class="additional-count">+{{ companySearch().stockIndexIds.length - 1 }} more</span>
                      }
                    }
                  </mat-select-trigger>
                </mat-select>
              </mat-form-field>
              <button mat-icon-button type="button" (click)="selectAllStockIndices()"
                      matTooltip="Select all indices" tabindex="-1">
                <mat-icon>select_all</mat-icon>
              </button>
              <button mat-icon-button type="button" (click)="deselectAllStockIndices()"
                      matTooltip="Deselect all indices" tabindex="-1">
                <mat-icon>deselect</mat-icon>
              </button>
            </div>
          </div>

          <div class="search-actions">
            <button mat-flat-button color="primary" type="submit"
                    [disabled]="companiesLoading() || !canSearchCompanies()"
                    [matTooltip]="!canSearchCompanies() ? 'Pick a country or apply at least one other filter (listing status alone is not enough).' : ''">
              <mat-icon>search</mat-icon>
              Search
            </button>
            <button mat-flat-button type="button" class="nrg-error-flat-button" (click)="clearCompanySearch()">
              <mat-icon>clear</mat-icon>
              Clear
            </button>
          </div>
        </form>
      </div>

      @if (hasSearched() || selectedCompanyCount() > 0) {
        <div class="results-box component-box">
          <div class="results-header">
            <span>
              {{ companiesTotal() }} result{{ companiesTotal() === 1 ? '' : 's' }}
            </span>
            <span class="spacer"></span>
            <span class="selection-count">
              <mat-icon>check_circle</mat-icon>
              {{ selectedCompanyCount() }} selected
            </span>
            @if (companiesTotal() > 0) {
              <button mat-stroked-button type="button" (click)="selectAllMatching()"
                      [matTooltip]="'Adds every company that matches your current filters (across all pages) to the selection.'">
                <mat-icon>done_all</mat-icon>
                Select all {{ companiesTotal() }} matching
              </button>
            }
            @if (selectedCompanyCount() > 0) {
              <button mat-stroked-button type="button" (click)="clearSelectedCompanies()">
                <mat-icon>close</mat-icon> Clear selection
              </button>
            }
          </div>

          @if (companyResults().length > 0) {
            <table mat-table matSort
                   [dataSource]="companyResults()"
                   [matSortActive]="companiesSortBy()"
                   [matSortDirection]="companiesSortAsc() ? 'asc' : 'desc'"
                   (matSortChange)="onCompaniesSortChange($event)"
                   class="results-table">
              <ng-container matColumnDef="select">
                <th mat-header-cell *matHeaderCellDef>
                  <mat-checkbox
                    [checked]="areAllVisibleCompaniesSelected()"
                    [indeterminate]="areSomeVisibleCompaniesSelected()"
                    (change)="toggleAllVisibleCompanies()">
                  </mat-checkbox>
                </th>
                <td mat-cell *matCellDef="let row">
                  <mat-checkbox
                    [checked]="isCompanySelected(row.id)"
                    (change)="toggleCompany(row)"
                    (click)="$event.stopPropagation()">
                  </mat-checkbox>
                </td>
              </ng-container>
              <ng-container matColumnDef="code">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Internal ID</th>
                <td mat-cell *matCellDef="let row">{{ row.code }}</td>
              </ng-container>
              <ng-container matColumnDef="companyName">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Company</th>
                <td mat-cell *matCellDef="let row">{{ row.companyName }}</td>
              </ng-container>
              <ng-container matColumnDef="stockSymbol">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Stock Symbol</th>
                <td mat-cell *matCellDef="let row">{{ row.stockSymbol }}</td>
              </ng-container>
              <ng-container matColumnDef="countryName">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Country</th>
                <td mat-cell *matCellDef="let row">{{ row.countryName }}</td>
              </ng-container>
              <ng-container matColumnDef="stockExchangeName">
                <th mat-header-cell *matHeaderCellDef>Stock Exchange</th>
                <td mat-cell *matCellDef="let row">{{ row.stockExchangeName }}</td>
              </ng-container>
              <ng-container matColumnDef="sectorName">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Sector</th>
                <td mat-cell *matCellDef="let row">{{ row.sectorName }}</td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="companyColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: companyColumns;"
                  (click)="toggleCompany(row)"
                  class="results-row">
              </tr>
            </table>
            <mat-paginator
              [length]="companiesTotal()"
              [pageSize]="companiesPageSize()"
              [pageIndex]="companiesPageIndex()"
              [pageSizeOptions]="[5, 10, 25, 50]"
              (page)="onCompaniesPageChange($event)">
            </mat-paginator>
          } @else if (!companiesLoading()) {
            <p class="empty-state">No companies match the current filters.</p>
          }
        </div>
      } @else {
        <p class="empty-state">Run a search to see results.</p>
      }
    </mat-step>

    <!-- ──────────────────────────────────────────────────────── STEP 4: DATA TYPE -->
    <mat-step [stepControl]="dataTypeForm">
      <ng-template matStepLabel>Data Type</ng-template>

      <div class="step-actions">
        <button mat-stroked-button matStepperPrevious>
          <mat-icon>arrow_back</mat-icon> Back
        </button>
        <span class="spacer"></span>
        <button mat-raised-button color="primary" matStepperNext [disabled]="dataTypeForm.invalid">
          Next <mat-icon iconPositionEnd>arrow_forward</mat-icon>
        </button>
      </div>

      <form [formGroup]="dataTypeForm" class="step-content component-box">
        <h3 class="step-title">What kind of data do you want to export?</h3>
        <p class="step-hint">Each option maps to its own backend endpoint and accepts a different set of variables.</p>

        <mat-radio-group formControlName="dataType" class="selection-cards">
          @for (opt of dataTypeOptions; track opt.kind) {
            @let available = isDataKindAvailable(opt.kind);
            <label class="selection-card"
                   [class.selected]="dataType === opt.kind"
                   [class.disabled]="!available"
                   [matTooltip]="available ? '' : 'Your customer profile has no categories that map to this report.'">
              <mat-radio-button [value]="opt.kind" class="selection-card__radio" [disabled]="!available"></mat-radio-button>
              <mat-icon class="selection-card__icon">{{ opt.icon }}</mat-icon>
              <div class="selection-card__body">
                <div class="selection-card__title">{{ opt.title }}</div>
                <div class="selection-card__desc">{{ opt.description }}</div>
              </div>
            </label>
          }
        </mat-radio-group>
      </form>
    </mat-step>

    <!-- ──────────────────────────────────────────────────────── STEP 5: VARIABLES -->
    <mat-step [stepControl]="variablesForm">
      <ng-template matStepLabel>Variables</ng-template>

      <div class="step-actions">
        <button mat-stroked-button matStepperPrevious>
          <mat-icon>arrow_back</mat-icon> Back
        </button>
        <span class="spacer"></span>
        <button mat-raised-button color="primary" matStepperNext [disabled]="variablesForm.invalid"
          [matTooltip]="variablesForm.invalid ? 'Select at least one variable' : ''">
          Next <mat-icon iconPositionEnd>arrow_forward</mat-icon>
        </button>
      </div>

      <div class="step-content component-box">
        <div class="step-title-row">
          <h3 class="step-title">Pick the variables to include</h3>
          <span class="selection-count">
            <mat-icon>check_circle</mat-icon>
            {{ selectedVariableCount() }} selected
          </span>
        </div>

        @if (isCategoriesLoading()) {
          <p class="empty-state">Loading variables…</p>
        } @else if (!dataType) {
          <p class="empty-state">Select a data type first.</p>
        } @else if (variableGroups().length === 0) {
          <p class="empty-state">No variables available for this report. Check the customer's category access.</p>
        } @else {
          @let activeTab = activeVariableTab();
          <mat-tab-group mat-stretch-tabs="false" mat-align-tabs="start" class="variables-tabs">
            @for (tab of variableGroups(); track tab.id) {
              @let locked = activeTab !== null && activeTab.id !== tab.id;
              <mat-tab>
                <ng-template mat-tab-label>
                  <span [class.tab-label--locked]="locked">{{ tab.name }}</span>
                  @if (areAllInGroupSelected(tab)) { <mat-icon class="tab-check">check_circle</mat-icon> }
                  @if (locked) {
                    <mat-icon class="tab-lock"
                              matTooltip="Only one tab's variables can be selected at a time. Clear the active tab to unlock this one.">
                      lock
                    </mat-icon>
                  }
                </ng-template>
                <div class="variables-tab-content">
                  @if (locked) {
                    <div class="tab-lock-hint">
                      <mat-icon>info</mat-icon>
                      <span>
                        Only variables from the <strong>{{ activeTab!.name }}</strong> tab can be selected.
                        Clear that tab to switch.
                      </span>
                      <button mat-stroked-button type="button" (click)="clearVariableSelection()"
                              matTooltip="Clears every variable selected on the active tab so this tab becomes available.">
                        <mat-icon>close</mat-icon>
                        Clear selection
                      </button>
                    </div>
                  }
                  <div class="group-actions">
                    <button mat-stroked-button (click)="toggleAllInGroup(tab)"
                            [disabled]="locked"
                            [class.tab-toggle--clear]="areAllInGroupSelected(tab)">
                      {{ areAllInGroupSelected(tab) ? 'Clear all in tab' : 'Select all in tab' }}
                    </button>
                  </div>
                  @for (section of tab.sections; track section.name) {
                    <section class="section-container nrg-border-radius-second-level">
                      @if (tab.sections.length > 1) {
                        <div class="section-header">{{ section.name }}</div>
                      }
                      <div class="section-content">
                        <div class="variables-list">
                          <div class="variables-list__row variables-list__row--head">
                            <span class="variables-list__cell variables-list__cell--check"></span>
                            <span class="variables-list__cell variables-list__cell--title">Variable</span>
                            <span class="variables-list__cell variables-list__cell--desc">Definition</span>
                          </div>
                          @for (v of section.variables; track v.id) {
                            <label class="variables-list__row" [class.variables-list__row--locked]="locked">
                              <span class="variables-list__cell variables-list__cell--check">
                                <mat-checkbox
                                  [checked]="isVariableSelected(v.id)"
                                  [disabled]="locked"
                                  (change)="toggleVariable(v.id)">
                                </mat-checkbox>
                              </span>
                              <span class="variables-list__cell variables-list__cell--title">{{ v.title }}</span>
                              <span class="variables-list__cell variables-list__cell--desc">
                                {{ v.description || v.explanation || '—' }}
                              </span>
                            </label>
                          }
                        </div>
                      </div>
                    </section>
                  }
                </div>
              </mat-tab>
            }
          </mat-tab-group>
        }
      </div>
    </mat-step>

    <!-- ──────────────────────────────────────────────────────── STEP 6: OPTIONS -->
    <mat-step [stepControl]="optionsForm">
      <ng-template matStepLabel>Options</ng-template>

      <div class="step-actions">
        <button mat-stroked-button matStepperPrevious>
          <mat-icon>arrow_back</mat-icon> Back
        </button>
        <span class="spacer"></span>
        <button mat-raised-button color="primary" matStepperNext>
          Next <mat-icon iconPositionEnd>arrow_forward</mat-icon>
        </button>
      </div>

      <form [formGroup]="optionsForm" class="step-content component-box">
        <h3 class="step-title">Export options</h3>
        <p class="step-hint">All toggles are optional.</p>

        <div class="options-section-header">
          <h4 class="options-section-title">Display</h4>
          <button mat-stroked-button type="button" (click)="toggleAllDisplayOptions()">
            {{ areAllDisplayOptionsSelected() ? 'Clear all' : 'Select all' }}
          </button>
        </div>
        <div class="options-grid">
          @for (opt of displayOptions; track opt.id) {
            <mat-checkbox [formControlName]="opt.id" class="option-checkbox">
              <span class="option-label">{{ opt.label }}</span>
            </mat-checkbox>
          }
        </div>

        @if (isInternalCustomer()) {
          <section class="checked-options">
            <div class="checked-options__header">
              <mat-icon class="checked-options__icon">shield_person</mat-icon>
              <h4 class="options-section-title">Checked</h4>
              <span class="checked-options__badge">Internal only</span>
            </div>
            <p class="checked-options__hint">
              These columns expose internal review status and are available only to internal customers.
            </p>
            <div class="options-grid">
              @for (opt of checkedOptions; track opt.id) {
                <mat-checkbox [formControlName]="opt.id" class="option-checkbox">
                  <span class="option-label">{{ opt.label }}</span>
                </mat-checkbox>
              }
            </div>
          </section>
        }
      </form>
    </mat-step>

    <!-- ──────────────────────────────────────────────────────── STEP 7: REVIEW & EXPORT -->
    <!-- matStepContent: body is only instantiated when the user navigates here.
         Without it, Angular re-renders this huge chip list on every selection
         signal update (15k chips → minute-long freeze on "Select all matching"). -->
    <mat-step>
      <ng-template matStepLabel>Review &amp; Export</ng-template>

      <ng-template matStepContent>
        @let companies = selectedCompaniesList();
        @let companiesShown = companies.slice(0, REVIEW_CHIP_LIMIT);
        @let companiesExtra = companies.length - companiesShown.length;
        @let variables = selectedVariableLabels();
        @let variablesShown = variables.slice(0, REVIEW_CHIP_LIMIT);
        @let variablesExtra = variables.length - variablesShown.length;

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon> Back
          </button>
          <span class="spacer"></span>
          <button mat-raised-button color="primary" (click)="exportReport()"
                  [disabled]="exportRunning() || modeForm.invalid || yearsForm.invalid || companiesForm.invalid || dataTypeForm.invalid || variablesForm.invalid">
            <mat-icon>download</mat-icon>
            Export Report
          </button>
        </div>

        <div class="step-content component-box review-grid">
          <section class="review-section">
            <div class="review-section__title"><mat-icon>tune</mat-icon> Mode</div>
            <div class="review-section__body">
              @switch (mode) {
                @case ('time-series') { Time Series }
                @case ('static') { Static Request }
                @default { <span class="muted">—</span> }
              }
            </div>
          </section>

          <section class="review-section">
            <div class="review-section__title"><mat-icon>event</mat-icon> Year(s)</div>
            <div class="review-section__body">{{ yearsSummary() }}</div>
          </section>

          <section class="review-section">
            <div class="review-section__title">
              <mat-icon>business</mat-icon> Companies ({{ companies.length }})
            </div>
            <div class="review-section__body chips scrollable">
              @for (co of companiesShown; track co.id) {
                <mat-chip-set>
                  <mat-chip>{{ co.companyName }}</mat-chip>
                </mat-chip-set>
              } @empty {
                <span class="muted">No companies selected</span>
              }
              @if (companiesExtra > 0) {
                <span class="more-hint">+ {{ companiesExtra }} more</span>
              }
            </div>
          </section>

          <section class="review-section">
            <div class="review-section__title"><mat-icon>category</mat-icon> Data Type</div>
            <div class="review-section__body">
              @if (dataType) { {{ dataTypeTitle(dataType) }} } @else { <span class="muted">—</span> }
            </div>
          </section>

          <section class="review-section">
            <div class="review-section__title">
              <mat-icon>checklist</mat-icon> Variables ({{ variables.length }})
            </div>
            <div class="review-section__body chips scrollable">
              @for (v of variablesShown; track v.label) {
                <mat-chip-set>
                  <mat-chip>{{ v.group }} · {{ v.label }}</mat-chip>
                </mat-chip-set>
              } @empty {
                <span class="muted">No variables selected</span>
              }
              @if (variablesExtra > 0) {
                <span class="more-hint">+ {{ variablesExtra }} more</span>
              }
            </div>
          </section>

          <section class="review-section">
            <div class="review-section__title"><mat-icon>tune</mat-icon> Options</div>
            <div class="review-section__body chips">
              @for (opt of enabledOptions(); track opt.id) {
                <mat-chip-set>
                  <mat-chip>{{ opt.label }}</mat-chip>
                </mat-chip-set>
              } @empty {
                <span class="muted">No options enabled</span>
              }
            </div>
          </section>
        </div>

        @if (exportRunning()) {
          <div class="step-content component-box preview-box">
            <p class="empty-state">Generating report…</p>
          </div>
        }
      </ng-template>
    </mat-step>
  </mat-stepper>
</div>
